import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, clearToken, resolveImage } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ExternalLink, LogOut, Home, Quote, Film, Play, FileText, EyeOff, Trophy, Calendar, Mail, Phone } from "lucide-react";

const Badge = ({ status }) => {
  const map = {
    Active: "bg-black text-white",
    "Coming Soon": "bg-gray-200 text-black",
    Pending: "bg-gray-700 text-white",
    Sold: "bg-white text-black border border-black",
  };
  return (
    <span
      className={`inline-block px-3 py-1 text-[10px] tracking-[0.24em] uppercase ${
        map[status] || "bg-gray-100 text-black"
      }`}
    >
      {status || "—"}
    </span>
  );
};

const REQUEST_STATUSES = ["New", "Contacted", "Scheduled", "Closed"];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [socialVideos, setSocialVideos] = useState([]);
  const [posts, setPosts] = useState([]);
  const [gamePlan, setGamePlan] = useState([]);
  const [showingRequests, setShowingRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [l, t, v, p, g, s] = await Promise.all([
        api.get("/admin/listings"),
        api.get("/admin/testimonials"),
        api.get("/admin/social-videos"),
        api.get("/admin/posts"),
        api.get("/admin/game-plan"),
        api.get("/admin/showing-requests"),
      ]);
      setListings(l.data);
      setTestimonials(t.data);
      setSocialVideos(v.data);
      setPosts(p.data);
      setGamePlan(g.data);
      setShowingRequests(s.data);
    } catch {
      toast.error("Could not load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDelete = async (id, address) => {
    if (!window.confirm(`Delete listing at ${address}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/listings/${id}`);
      toast.success("Listing deleted.");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const logout = () => {
    clearToken();
    navigate("/admin/login");
  };

  const handleRequestStatusChange = async (id, status) => {
    try {
      await api.put(`/admin/showing-requests/${id}`, { status });
      setShowingRequests((rows) => rows.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success("Status updated.");
    } catch {
      toast.error("Could not update status");
    }
  };

  const handleDeleteRequest = async (id, address) => {
    if (!window.confirm(`Delete showing request for ${address}?`)) return;
    try {
      await api.delete(`/admin/showing-requests/${id}`);
      toast.success("Request deleted.");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-white pb-32">
      {/* Header */}
      <header className="border-b border-black/10 px-6 md:px-12 py-6 flex items-center justify-between sticky top-0 bg-white z-10">
        <div className="flex items-center gap-4">
          <p className="font-heading text-2xl tracking-[0.18em] uppercase">Manzano</p>
          <span className="text-[10px] tracking-[0.3em] uppercase text-black/40 hidden sm:inline">
            · Admin
          </span>
        </div>
        <div className="flex items-center gap-6">
          <Link
            to="/"
            data-testid="admin-view-site"
            className="text-[11px] uppercase tracking-[0.24em] flex items-center gap-2 hover:opacity-60"
          >
            <Home size={14} /> View Site
          </Link>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="text-[11px] uppercase tracking-[0.24em] flex items-center gap-2 hover:opacity-60"
          >
            <LogOut size={14} /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-16">
        {/* ===========================  Private Showing Requests  =========================== */}
        <div className="flex items-end justify-between mb-12 gap-6">
          <div>
            <p className="overline mb-4">Leads</p>
            <h1 className="font-heading text-4xl md:text-5xl tracking-tight">
              Private Showing <em className="italic font-light">Requests</em>
            </h1>
          </div>
          {showingRequests.length > 0 && (
            <p className="text-[11px] uppercase tracking-[0.24em] text-black/40">
              {showingRequests.filter((r) => r.status === "New").length} new
            </p>
          )}
        </div>

        {loading ? (
          <p className="text-sm text-black/40">Loading...</p>
        ) : showingRequests.length === 0 ? (
          <div className="border border-dashed border-black/15 px-8 py-20 text-center">
            <Mail size={20} className="mx-auto mb-4 text-black/30" strokeWidth={1.25} />
            <p className="text-black/50">No showing requests yet.</p>
          </div>
        ) : (
          <div className="border-t border-black/10">
            {showingRequests.map((r) => (
              <div
                key={r.id}
                data-testid={`admin-showing-request-row-${r.id}`}
                className="grid grid-cols-12 gap-4 py-6 border-b border-black/10 items-center"
              >
                <div className="col-span-12 md:col-span-4">
                  <p className="font-medium text-sm">{r.name}</p>
                  <p className="text-[11px] tracking-wider uppercase text-black/40 mt-1 truncate">
                    {r.listing_address}
                  </p>
                  <p className="text-xs text-black/60 mt-2 flex items-center gap-2">
                    <Mail size={12} className="shrink-0" />
                    <a href={`mailto:${r.email}`} className="hover:underline truncate">{r.email}</a>
                  </p>
                  {r.phone && (
                    <p className="text-xs text-black/60 mt-1 flex items-center gap-2">
                      <Phone size={12} className="shrink-0" />
                      <a href={`tel:${r.phone}`} className="hover:underline">{r.phone}</a>
                    </p>
                  )}
                </div>
                <div className="col-span-12 md:col-span-3 text-xs text-black/70">
                  {(r.preferred_date || r.preferred_time) ? (
                    <p className="flex items-center gap-2">
                      <Calendar size={12} className="shrink-0 text-black/40" />
                      {[r.preferred_date, r.preferred_time].filter(Boolean).join(" at ")}
                    </p>
                  ) : (
                    <p className="text-black/30">No date preference</p>
                  )}
                  {r.message && (
                    <p className="text-black/50 mt-2 line-clamp-2 italic">"{r.message}"</p>
                  )}
                  {!r.email_sent && (
                    <p className="text-[10px] tracking-[0.2em] uppercase text-red-500/70 mt-2">Email not sent</p>
                  )}
                  {!r.fub_sent && (
                    <p className="text-[10px] tracking-[0.2em] uppercase text-red-500/70 mt-1">CRM not synced</p>
                  )}
                </div>
                <div className="col-span-6 md:col-span-3">
                  <select
                    value={r.status || "New"}
                    onChange={(e) => handleRequestStatusChange(r.id, e.target.value)}
                    data-testid={`admin-showing-request-status-${r.id}`}
                    className="text-[11px] uppercase tracking-[0.2em] border border-black/15 px-3 py-2 bg-white focus:outline-none focus:border-black"
                  >
                    {REQUEST_STATUSES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="col-span-6 md:col-span-2 flex md:justify-end items-center gap-3">
                  <Link
                    to={`/listings/${r.listing_slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`admin-view-listing-${r.id}`}
                    className="p-2 hover:bg-black/5 rounded"
                    aria-label="View listing"
                  >
                    <ExternalLink size={14} />
                  </Link>
                  <button
                    onClick={() => handleDeleteRequest(r.id, r.listing_address)}
                    data-testid={`admin-delete-showing-request-${r.id}`}
                    className="p-2 hover:bg-red-50 hover:text-red-600 rounded"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ===========================  Properties  =========================== */}
        <div className="flex items-end justify-between mt-24 mb-12 gap-6">
          <div>
            <p className="overline mb-4">Properties</p>
            <h1 className="font-heading text-4xl md:text-5xl tracking-tight">
              Portfolio <em className="italic font-light">Manager</em>
            </h1>
          </div>
          <Link
            to="/admin/listings/new"
            data-testid="admin-new-listing"
            className="inline-flex items-center gap-2 px-8 py-4 bg-black text-white border border-black text-[11px] uppercase tracking-[0.28em] hover:bg-white hover:text-black transition-all duration-500"
          >
            <Plus size={16} /> New Listing
          </Link>
        </div>

        {loading ? (
          <p className="text-sm text-black/40">Loading...</p>
        ) : listings.length === 0 ? (
          <div className="border border-dashed border-black/15 px-8 py-24 text-center">
            <p className="text-black/50 mb-6">No listings yet.</p>
            <Link
              to="/admin/listings/new"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] underline underline-offset-4"
            >
              <Plus size={14} /> Add your first listing
            </Link>
          </div>
        ) : (
          <div className="border-t border-black/10">
            {listings.map((l) => (
              <div
                key={l.id}
                data-testid={`admin-listing-row-${l.id}`}
                className="grid grid-cols-12 gap-4 py-6 border-b border-black/10 items-center"
              >
                <div className="col-span-12 md:col-span-2">
                  {l.image_urls?.[0] ? (
                    <img
                      src={resolveImage(l.image_urls[0])}
                      alt={l.address}
                      className="w-full aspect-[4/3] object-cover"
                    />
                  ) : (
                    <div className="w-full aspect-[4/3] bg-gray-100 flex items-center justify-center text-[10px] text-black/30 uppercase tracking-widest">
                      No photo
                    </div>
                  )}
                </div>
                <div className="col-span-12 md:col-span-5">
                  <p className="font-heading text-2xl tracking-tight">{l.price || "—"}</p>
                  <p className="text-sm text-black/70 mt-1">{l.address || "Untitled"}</p>
                  <p className="text-[11px] tracking-wider uppercase text-black/40 mt-1">
                    {[l.city, l.state, l.zip_code].filter(Boolean).join(", ")}
                  </p>
                </div>
                <div className="col-span-6 md:col-span-2 text-xs text-black/70">
                  {l.beds || "—"} bd · {l.baths || "—"} ba
                  <br />
                  {l.sqft || "—"} sq ft
                </div>
                <div className="col-span-6 md:col-span-1">
                  <Badge status={l.status} />
                  {l.featured && (
                    <p className="text-[9px] tracking-[0.24em] uppercase text-black/40 mt-2">
                      ★ Featured
                    </p>
                  )}
                </div>
                <div className="col-span-12 md:col-span-2 flex md:justify-end items-center gap-3">
                  {l.source_url && (
                    <a
                      href={l.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 hover:bg-black/5 rounded"
                      title="View source"
                    >
                      <ExternalLink size={14} />
                    </a>
                  )}
                  <Link
                    to={`/admin/listings/${l.id}/edit`}
                    data-testid={`admin-edit-${l.id}`}
                    className="p-2 hover:bg-black/5 rounded"
                  >
                    <Pencil size={14} />
                  </Link>
                  <button
                    onClick={() => handleDelete(l.id, l.address)}
                    data-testid={`admin-delete-${l.id}`}
                    className="p-2 hover:bg-red-50 hover:text-red-600 rounded"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ===========================  Testimonials  =========================== */}
        <div className="flex items-end justify-between mt-24 mb-12 gap-6">
          <div>
            <p className="overline mb-4">Social Proof</p>
            <h2 className="font-heading text-3xl md:text-4xl tracking-tight">
              Testimonials
            </h2>
          </div>
          <Link
            to="/admin/testimonials/new"
            data-testid="admin-new-testimonial"
            className="inline-flex items-center gap-2 px-8 py-4 bg-black text-white border border-black text-[11px] uppercase tracking-[0.28em] hover:bg-white hover:text-black transition-all duration-500"
          >
            <Plus size={16} /> New Testimonial
          </Link>
        </div>

        {loading ? (
          <p className="text-sm text-black/40">Loading...</p>
        ) : testimonials.length === 0 ? (
          <div className="border border-dashed border-black/15 px-8 py-20 text-center">
            <Quote size={20} className="mx-auto mb-4 text-black/30" strokeWidth={1.25} />
            <p className="text-black/50 mb-6">No testimonials yet.</p>
            <Link
              to="/admin/testimonials/new"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] underline underline-offset-4"
            >
              <Plus size={14} /> Add your first testimonial
            </Link>
          </div>
        ) : (
          <div className="border-t border-black/10">
            {testimonials.map((t) => (
              <div
                key={t.id}
                data-testid={`admin-testimonial-row-${t.id}`}
                className="grid grid-cols-12 gap-4 py-6 border-b border-black/10 items-center"
              >
                <div className="col-span-12 md:col-span-1">
                  {t.photo_url ? (
                    <img
                      src={resolveImage(t.photo_url)}
                      alt={t.client_name}
                      className="w-14 h-14 rounded-full object-cover grayscale"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center text-xs text-black/40 font-heading">
                      {(t.client_name || "?").charAt(0)}
                    </div>
                  )}
                </div>
                <div className="col-span-12 md:col-span-7">
                  <p className="font-medium text-sm">{t.client_name || "Untitled"}</p>
                  <p className="text-[11px] tracking-wider uppercase text-black/40 mt-1">
                    {[t.city, t.year, t.transaction_type].filter(Boolean).join(" · ")}
                  </p>
                  <p className="text-xs text-black/60 mt-2 line-clamp-2 italic">
                    "{t.quote}"
                  </p>
                </div>
                <div className="col-span-6 md:col-span-2 text-xs text-black/60">
                  {t.context || "—"}
                </div>
                <div className="col-span-6 md:col-span-2 flex md:justify-end items-center gap-3">
                  {t.featured && (
                    <span className="text-[9px] tracking-[0.24em] uppercase text-black/40">★ Live</span>
                  )}
                  <Link
                    to={`/admin/testimonials/${t.id}/edit`}
                    data-testid={`admin-edit-testimonial-${t.id}`}
                    className="p-2 hover:bg-black/5 rounded"
                  >
                    <Pencil size={14} />
                  </Link>
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Delete testimonial from ${t.client_name}?`)) return;
                      try {
                        await api.delete(`/admin/testimonials/${t.id}`);
                        toast.success("Testimonial deleted.");
                        load();
                      } catch {
                        toast.error("Delete failed");
                      }
                    }}
                    data-testid={`admin-delete-testimonial-${t.id}`}
                    className="p-2 hover:bg-red-50 hover:text-red-600 rounded"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ===========================  Social Videos  =========================== */}
        <div className="flex items-end justify-between mt-24 mb-12 gap-6">
          <div>
            <p className="overline mb-4">Reels & Tours</p>
            <h2 className="font-heading text-3xl md:text-4xl tracking-tight">
              Social Videos
            </h2>
          </div>
          <Link
            to="/admin/social-videos/new"
            data-testid="admin-new-social-video"
            className="inline-flex items-center gap-2 px-8 py-4 bg-black text-white border border-black text-[11px] uppercase tracking-[0.28em] hover:bg-white hover:text-black transition-all duration-500"
          >
            <Plus size={16} /> New Video
          </Link>
        </div>

        {loading ? (
          <p className="text-sm text-black/40">Loading...</p>
        ) : socialVideos.length === 0 ? (
          <div className="border border-dashed border-black/15 px-8 py-20 text-center">
            <Film size={20} className="mx-auto mb-4 text-black/30" strokeWidth={1.25} />
            <p className="text-black/50 mb-6">No social videos yet — the homepage will use default placeholders.</p>
            <Link
              to="/admin/social-videos/new"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] underline underline-offset-4"
            >
              <Plus size={14} /> Add your first video
            </Link>
          </div>
        ) : (
          <div className="border-t border-black/10">
            {socialVideos.map((v) => (
              <div
                key={v.id}
                data-testid={`admin-social-video-row-${v.id}`}
                className="grid grid-cols-12 gap-4 py-6 border-b border-black/10 items-center"
              >
                <div className="col-span-12 md:col-span-1">
                  <div className="relative w-16 aspect-[9/16] bg-black overflow-hidden">
                    {v.video_url ? (
                      <video
                        src={`${resolveImage(v.video_url)}#t=0.5`}
                        poster={v.poster_url ? resolveImage(v.poster_url) : undefined}
                        preload="metadata"
                        muted
                        playsInline
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    ) : v.poster_url ? (
                      <img
                        src={resolveImage(v.poster_url)}
                        alt={v.title}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-[9px] text-white/40 uppercase">
                        Empty
                      </div>
                    )}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Play size={14} className="text-white" fill="white" />
                    </div>
                  </div>
                </div>
                <div className="col-span-12 md:col-span-7">
                  <p className="font-medium text-sm">{v.title || "Untitled"}</p>
                  <p className="text-[11px] tracking-wider uppercase text-black/40 mt-1">
                    {[v.handle, v.views && `${v.views} views`].filter(Boolean).join(" · ")}
                  </p>
                  {v.video_url && (
                    <p className="text-[10px] text-black/30 mt-1 truncate max-w-md">{v.video_url}</p>
                  )}
                </div>
                <div className="col-span-6 md:col-span-2 text-xs text-black/60">
                  Position: {v.position ?? 0}
                </div>
                <div className="col-span-6 md:col-span-2 flex md:justify-end items-center gap-3">
                  {v.featured && (
                    <span className="text-[9px] tracking-[0.24em] uppercase text-black/40">★ Live</span>
                  )}
                  <Link
                    to={`/admin/social-videos/${v.id}/edit`}
                    data-testid={`admin-edit-social-video-${v.id}`}
                    className="p-2 hover:bg-black/5 rounded"
                  >
                    <Pencil size={14} />
                  </Link>
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Delete video "${v.title || "Untitled"}"?`)) return;
                      try {
                        await api.delete(`/admin/social-videos/${v.id}`);
                        toast.success("Video deleted.");
                        load();
                      } catch {
                        toast.error("Delete failed");
                      }
                    }}
                    data-testid={`admin-delete-social-video-${v.id}`}
                    className="p-2 hover:bg-red-50 hover:text-red-600 rounded"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ===========================  Blog Posts  =========================== */}
        <div className="flex items-end justify-between mt-24 mb-12 gap-6">
          <div>
            <p className="overline mb-4">Insights &amp; Field Notes</p>
            <h2 className="font-heading text-3xl md:text-4xl tracking-tight">
              Blog Posts
            </h2>
          </div>
          <Link
            to="/admin/posts/new"
            data-testid="admin-new-post"
            className="inline-flex items-center gap-2 px-8 py-4 bg-black text-white border border-black text-[11px] uppercase tracking-[0.28em] hover:bg-white hover:text-black transition-all duration-500"
          >
            <Plus size={16} /> New Post
          </Link>
        </div>

        {loading ? (
          <p className="text-sm text-black/40">Loading…</p>
        ) : posts.length === 0 ? (
          <div className="border border-dashed border-black/15 px-8 py-20 text-center">
            <FileText size={20} className="mx-auto mb-4 text-black/30" strokeWidth={1.25} />
            <p className="text-black/50 mb-6">No posts yet.</p>
            <Link
              to="/admin/posts/new"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] underline underline-offset-4"
            >
              <Plus size={14} /> Write your first post
            </Link>
          </div>
        ) : (
          <div className="border-t border-black/10">
            {posts.map((p) => {
              const pd = p.publish_date ? new Date(p.publish_date) : null;
              const dateLabel = pd
                ? pd.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                : "";
              return (
                <div
                  key={p.id}
                  data-testid={`admin-post-row-${p.id}`}
                  className="grid grid-cols-12 gap-4 py-6 border-b border-black/10 items-center"
                >
                  <div className="col-span-12 md:col-span-1">
                    {p.hero_image_url ? (
                      <div className="w-20 aspect-[4/3] bg-gray-100 overflow-hidden">
                        <img src={resolveImage(p.hero_image_url)} alt="" className="w-full h-full object-cover" />
                      </div>
                    ) : (
                      <div className="w-20 aspect-[4/3] bg-gray-100" />
                    )}
                  </div>
                  <div className="col-span-12 md:col-span-7">
                    <p className="font-medium text-sm">{p.title}</p>
                    <p className="text-[11px] tracking-wider uppercase text-black/40 mt-1">
                      {[p.category, dateLabel].filter(Boolean).join(" · ")}
                    </p>
                    <p className="text-[11px] text-black/30 mt-1 truncate">/blog/{p.slug}</p>
                  </div>
                  <div className="col-span-6 md:col-span-2 text-xs">
                    {p.is_published ? (
                      <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.24em] uppercase text-black/50">
                        ● Published
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.24em] uppercase text-black/30">
                        <EyeOff size={11} /> Draft
                      </span>
                    )}
                  </div>
                  <div className="col-span-6 md:col-span-2 flex md:justify-end items-center gap-3">
                    {p.is_published && (
                      <a
                        href={`/blog/${p.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 hover:bg-black/5 rounded"
                        aria-label="View post"
                        data-testid={`admin-view-post-${p.id}`}
                      >
                        <ExternalLink size={14} />
                      </a>
                    )}
                    <Link
                      to={`/admin/posts/${p.id}/edit`}
                      data-testid={`admin-edit-post-${p.id}`}
                      className="p-2 hover:bg-black/5 rounded"
                    >
                      <Pencil size={14} />
                    </Link>
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Delete "${p.title}"?`)) return;
                        try {
                          await api.delete(`/admin/posts/${p.id}`);
                          toast.success("Post deleted.");
                          load();
                        } catch {
                          toast.error("Delete failed");
                        }
                      }}
                      data-testid={`admin-delete-post-${p.id}`}
                      className="p-2 hover:bg-red-50 hover:text-red-600 rounded"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ===========================  Game Plan  =========================== */}
        <div className="flex items-end justify-between mt-24 mb-12 gap-6">
          <div>
            <p className="overline mb-4">Video Campaign</p>
            <h2 className="font-heading text-3xl md:text-4xl tracking-tight">
              Game Plan
            </h2>
            <p className="text-xs text-black/50 mt-2">
              Four-quarter video series at <code className="text-[10px] bg-black/5 px-1">/gameplan</code>
            </p>
          </div>
          <Link
            to="/admin/game-plan/new"
            data-testid="admin-new-gameplan"
            className="inline-flex items-center gap-2 px-8 py-4 bg-black text-white border border-black text-[11px] uppercase tracking-[0.28em] hover:bg-white hover:text-black transition-all duration-500"
          >
            <Plus size={16} /> New Quarter
          </Link>
        </div>

        {loading ? (
          <p className="text-sm text-black/40">Loading…</p>
        ) : gamePlan.length === 0 ? (
          <div className="border border-dashed border-black/15 px-8 py-20 text-center">
            <Trophy size={20} className="mx-auto mb-4 text-black/30" strokeWidth={1.25} />
            <p className="text-black/50">No quarters yet.</p>
          </div>
        ) : (
          <div className="border-t border-black/10">
            {gamePlan.map((g) => (
              <div
                key={g.id}
                data-testid={`admin-gameplan-row-${g.id}`}
                className="grid grid-cols-12 gap-4 py-6 border-b border-black/10 items-center"
              >
                <div className="col-span-12 md:col-span-1">
                  <div className="w-14 h-14 border border-black/15 flex flex-col items-center justify-center">
                    <span className="text-[9px] tracking-widest uppercase text-black/40">Q</span>
                    <span className="font-heading text-2xl leading-none">{g.quarter}</span>
                  </div>
                </div>
                <div className="col-span-12 md:col-span-7">
                  <p className="font-medium text-sm">{g.title}</p>
                  <p className="text-[11px] tracking-wider uppercase text-black/40 mt-1">
                    {g.subtitle}
                  </p>
                  {g.video_url ? (
                    <p className="text-[10px] tracking-[0.24em] uppercase text-black/40 mt-1">● Video uploaded</p>
                  ) : g.youtube_url ? (
                    <p className="text-[10px] text-black/30 mt-1 truncate max-w-md">{g.youtube_url}</p>
                  ) : (
                    <p className="text-[10px] tracking-[0.24em] uppercase text-black/30 mt-1">No video yet</p>
                  )}
                </div>
                <div className="col-span-6 md:col-span-2 text-xs">
                  {g.is_published ? (
                    <span className="text-[10px] tracking-[0.24em] uppercase text-black/50">● Published</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.24em] uppercase text-black/30">
                      <EyeOff size={11} /> Draft
                    </span>
                  )}
                </div>
                <div className="col-span-6 md:col-span-2 flex md:justify-end items-center gap-3">
                  <a
                    href={`/gameplan#`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 hover:bg-black/5 rounded"
                    aria-label="View live"
                    data-testid={`admin-view-gameplan-${g.id}`}
                  >
                    <ExternalLink size={14} />
                  </a>
                  <Link
                    to={`/admin/game-plan/${g.id}/edit`}
                    data-testid={`admin-edit-gameplan-${g.id}`}
                    className="p-2 hover:bg-black/5 rounded"
                  >
                    <Pencil size={14} />
                  </Link>
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Delete Q${g.quarter} "${g.title}"?`)) return;
                      try {
                        await api.delete(`/admin/game-plan/${g.id}`);
                        toast.success("Quarter deleted.");
                        load();
                      } catch { toast.error("Delete failed"); }
                    }}
                    data-testid={`admin-delete-gameplan-${g.id}`}
                    className="p-2 hover:bg-red-50 hover:text-red-600 rounded"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
};

export default AdminDashboard;
